import { Observable, forkJoin } from 'rxjs';
import { Injectable, Inject } from '@angular/core';
import { HttpClient } from "@angular/common/http";
import { ReportCache, ReportType } from 'core-lib';
import { API_URL_TOKEN } from '../tokens/tokens';

// interface Vector { vector_name: string, vector_id: number; }
// interface Crosstab extends CrosstabQuestion, Vector { };

export interface ServReqData {
    completion: number;
    control0?: number;
    start_date?: number;
    end_date?: number;
    filters: Array<number>;
    concept?: string;
    concept_first_position?: boolean;
}

/**
 * @description - Report cache service
 * @class ReportCacheService
 */
@Injectable({
    providedIn: 'root'
})
export class ReportCacheService {

    // readonly _CACHES: BehaviorSubject<ReportCache[]>;

    constructor(
        private http: HttpClient,
        @Inject(API_URL_TOKEN) private readonly apiUrl: string
    ) { }

    /**
     * @description Retrieve existing cache object.
     * @param cacheKey Key for existing cache object on the server.
     */
    getCache(cacheKey: string): Observable<ReportCache> {
        return this.http.get<ReportCache>(`${this.apiUrl}/reports/cache/${cacheKey}`);
    }

    updateCacheParams(cacheKey: string, params: { [id: string]: any }) {
        return this.http.put(`${this.apiUrl}/reports/cache/${cacheKey}/parameters`, params);
    }

    getCaches(cacheKeys: string[]): Observable<ReportCache[]> {
        return this.http.post<ReportCache[]>(`${this.apiUrl}/reports/caches`, {
            cache_list: cacheKeys
        });
    }

    exportTables(cacheKey: string) {
        return this.http.get<{ spreadsheet: string }>(`https://api.h2delta.com/reports/cache/${cacheKey}/export_tables`);
    }

    exportTable(cacheKey: string, tableId: string) {
        return this.http.get<{ spreadsheet: string }>(`https://api.h2delta.com/reports/cache/${cacheKey}/export_table/${tableId}`);
        // return this.http.get<{ spreadsheet: string }>(`${this.apiUrl}/reports/cache/${cacheKey}/export_table/${tableId}`);
    }

    runReport(cacheKey: string, reportType: ReportType, options: ServReqData, crosstab = null): Observable<ReportCache> {
        const params: any = { ...options, report_type: reportType };
        if (crosstab) {
            if (typeof crosstab === 'string') {
                params.question_id = crosstab;
            } else if (Number.isInteger(crosstab)) {
                params.vector_id = crosstab;
            }
        }

        return this.http.post<ReportCache>(`${this.apiUrl}/reports/cache/${cacheKey}/tabulate`, params);
    }

    async filter(cacheKey: string, reportType: ReportType, options: ServReqData, crosstab = null): Promise<ReportCache> {
        const filter$ = this.http.put<ReportCache>(`${this.apiUrl}/reports/cache/${cacheKey}/set_sample`, options);
        let reportParameterize$: Observable<ReportCache | [ReportCache, any]>;
        if (crosstab) {
            let cb;
            if (typeof crosstab === 'string') {
                cb = { question_id: crosstab };
            } else if (Number.isInteger(crosstab)) {
                cb = { vector_id: crosstab };
            }
            reportParameterize$ = forkJoin([filter$, this.updateCrosstab(cacheKey, cb)]);
        } else
            reportParameterize$ = filter$;
        await reportParameterize$.toPromise();
        return this.tabulate(cacheKey, reportType).toPromise();
    }

    retrieveCaches(clientId: string): ReportCache[] {
        const resetKey: string = `R_CACHES_RESET_${clientId}`,
            rCacheFieldId: string = `R_CACHE_${clientId}`,
            timestamp: number = 1599572515029;
        const lastResetStr = localStorage.getItem(resetKey);
        if (!lastResetStr) {
            localStorage.setItem(resetKey, 1599572515029 + '');
            localStorage.removeItem(rCacheFieldId);
            localStorage.setItem(resetKey, timestamp + '');
        } else {
            const lastReset: Date = new Date(Number(lastResetStr));
            if (lastReset.valueOf() < timestamp) {
                localStorage.removeItem(rCacheFieldId);
                localStorage.setItem(resetKey, timestamp + '');
            }
        }
        // Keep track of clears by counter. Starts at 0
        const clientCaches = localStorage.getItem(rCacheFieldId);
        return !!clientCaches ? JSON.parse(clientCaches) : [];
    }

    createArchive(cacheKey: string, name: string) {
        return this.http.post(`${this.apiUrl}/reports/archive`, {
            ckey: cacheKey, archive_name: name
        });
    }

    deleteArchive(archiveId: string) {
        return this.http.delete(`${this.apiUrl}/reports/archive/${archiveId}`);
    }

    tabulate(cacheKey: string, reportType: string): Observable<any> {
        if (reportType === 'summary') {
            return this.http.post(`${this.apiUrl}/reports/cache/${cacheKey}/tabulate`, { report_type: reportType });
        }
        return this.http.get(`${this.apiUrl}/reports/cache/${cacheKey}/tabulate/${reportType}`);
    }

    // FIXME: Change path to /reports/cache/:cKey/set_banner
    private updateCrosstab(cacheKey: string, thing: { vector_id?: number, question_id?: string }): Observable<any> {
        let data;
        if (thing.vector_id)
            data = { vector_id: thing.vector_id };
        else
            data = { crosstab_question: thing.question_id };

        return this.http.put(`${this.apiUrl}/reports/cache/${cacheKey}/set_banner`, data);
    }

    /**
     * Pass in the route for api call. Initial / must be ommitted.
     * @param route Path for REST api call.
     * @param cache option for $http service.
     */
    getReportData<T>(cacheKey: string, route: string): Observable<T> {
        return this.http.get<T>(`${this.apiUrl}/reports/cache/${cacheKey}/${route}`);
    }

    initCache(cellId: string, isArchive: boolean = false): Observable<ReportCache> {
        let url: string;
        if (isArchive)
            url = `${this.apiUrl}/reports/cache/archive/${cellId}`;
        else
            url = `${this.apiUrl}/reports/cache/cell/${cellId}`;
        return this.http.get<ReportCache>(url);
    }

    initCombinedReport(reportId: number, reportType: string = 'linescore') {
        return this.http.post<ReportCache>(`${this.apiUrl}/reports/cache`, {
            report_id: reportId,
            report_type: reportType
        });
    }

    updateStoredCaches(clientId: string, caches: ReportCache[]): void {
        localStorage.setItem(`R_CACHE_${clientId}`, JSON.stringify(caches));
    }

    // /**
    //  * Exports data to exell spreadsheets
    //  * @param queIds List of question ids
    //  * @param nmiencoded 
    //  */
    // async exportData(queIds: string[], nmiencoded: boolean): Promise<any> {
    //     const reportCache: ReportCache = this._reportCache.getValue();
    //     return this.http.post(`/api/reports/cache/${reportCache.cache_key}/export`, {
    //         json_data: { nmiencoded, questions: queIds }
    //     }).toPromise();
    // }

}